import React from 'react';
import { resolveItemKey } from '../utils/itemUtils';

const BASE_LOCKER_SLOTS = 8;

const ItemSelectionStep = ({
  items = [],
  selectedKeys = new Set(),
  pendingPrize = null,
  maxLockerSlots = BASE_LOCKER_SLOTS,
  loading = false,
  onToggleItem,
  onSelectAll,
  onClearSelection,
}) => {
  const itemsWithKeys = items.map((item, index) => {
    const key = resolveItemKey(item, index);
    return {key, item};
  });

  const selectedCount = selectedKeys.size;
  const lockerCount = itemsWithKeys.length;
  const finalCapacity = maxLockerSlots > BASE_LOCKER_SLOTS ? BASE_LOCKER_SLOTS : maxLockerSlots;
  const pendingCount = pendingPrize ? 1 : 0;
  const remainingCount = lockerCount - selectedCount;
  const finalOccupancy = remainingCount + pendingCount;
  const requiredToShip = Math.max(0, lockerCount + pendingCount - finalCapacity);
  const mustShipCount = requiredToShip > 0 ? requiredToShip : (pendingPrize ? 1 : 0);
  const selectionValid = selectedCount > 0 && finalOccupancy <= finalCapacity;
  const selectionShortfall = selectedCount < mustShipCount ? mustShipCount - selectedCount : 0;

  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between text-sm text-ponpon-navy/70">
        <span>
          You're shipping {selectedCount} {selectedCount === 1 ? 'item' : 'items'}.
        </span>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onSelectAll}
            disabled={loading}
            className="text-xs font-semibold text-ponpon-coral underline-offset-2 hover:underline disabled:opacity-50"
          >
            Select all
          </button>
          <button
            type="button"
            onClick={onClearSelection}
            disabled={loading}
            className="text-xs font-semibold text-ponpon-navy/60 underline-offset-2 hover:underline disabled:opacity-50"
          >
            Clear selection
          </button>
        </div>
      </div>
      <div className="grid max-h-72 gap-3 overflow-y-auto pr-1 sm:grid-cols-2">
        {itemsWithKeys.map(({key, item}) => {
          const prize = item?.prize || item;
          const prizeName = prize?.name || 'Locker prize';
          const prizeImg =
            typeof prize?.imageUrl === 'string' && prize.imageUrl.trim().length > 0
              ? prize.imageUrl.trim()
              : '/assets/ponpon-logo.svg';
          const checked = selectedKeys.has(key);
          return (
            <label
              key={key}
              className={`flex cursor-pointer items-center gap-3 rounded-2xl border p-3 transition ${
                checked
                  ? 'border-ponpon-coral/40 bg-ponpon-coral/10'
                  : 'border-ponpon-navy/10 bg-white/90 hover:border-ponpon-coral/30'
              } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-ponpon-navy/30 text-ponpon-coral focus:ring-ponpon-coral"
                checked={checked}
                onChange={() => onToggleItem(key)}
                disabled={loading}
              />
              <div className="flex items-center gap-3">
                <div className="flex h-14 w-14 items-center justify-center overflow-hidden rounded-xl">
                  <img src={prizeImg} alt={prizeName} className="max-h-full max-w-full object-contain" />
                </div>
                <div className="text-sm text-ponpon-navy/70">
                  <p className="font-semibold text-ponpon-navy">{prizeName}</p>
                  {item?.machineName ? (
                    <p className="text-xs text-ponpon-navy/55">{item.machineName}</p>
                  ) : null}
                </div>
              </div>
            </label>
          );
        })}
      </div>
      {!selectionValid ? (
        <p className="rounded-2xl bg-ponpon-coral/10 px-4 py-3 text-xs text-ponpon-coral/80">
          {selectedCount === 0
            ? 'Select at least one item to ship.'
            : `Select at least ${selectionShortfall || 1} more ${
                selectionShortfall === 1 ? 'item' : 'items'
              } to free enough space for your pending prize.`}
        </p>
      ) : null}
      {maxLockerSlots > BASE_LOCKER_SLOTS ? (
        <div className="flex items-center justify-end text-xs text-ponpon-navy/55">
          <span>The locker resets to 8 slots after this shipment.</span>
        </div>
      ) : null}
    </section>
  );
};

export default ItemSelectionStep;

